import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import PMSwitch from "../../../utils/PMSwitch";
import UppercaseFirstLetter, { dispatchWithToast } from "../../../Utils";
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  FolderIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { updateUserAsync } from "../../../../slices/user";
import { parseRoleName } from "../../../../utils/parseRoleName";
import { buildRoleFormPayloadFromTree } from "../../../../utils/buildRoleFormPayloadFromTree";
import { applyMainModuleToggle } from "../../../../utils/applyMainModuleToggle";
import { getCompanyUserRoleHelp } from "../../../../utils/companyUserRolesRoleHelp";
import { fetchRoles, fetchRoleTemplates } from "../../../../api/users";
import { fetchProjects } from "../../../../api/project";
import { companyHasActiveFeature } from "../../../../routes/roleOnly";
import { buildProjectsIndexParams } from "../../../../utils/projectsIndexParams";

const PROJECTS_ADMIN_ROLE = "projects_admin";

/** Compare project/role ids without number-vs-string false negatives. */
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

function RoleUserHelpText({ roleName, fallbackDescription = null }) {
  const { t, i18n } = useTranslation();
  const block = getCompanyUserRoleHelp(i18n.language, roleName);
  const hasCurated =
    block && (Boolean(block.includes) || Boolean(block.adminNote));
  if (hasCurated) {
    return (
      <div className="mt-1 space-y-1.5 text-xs leading-snug text-gray-600">
        {block.includes ? (
          <p>
            <span className="font-medium text-gray-700">
              {t("company.userRoles.helpLabel.scope")}
            </span>{" "}
            {block.includes}
          </p>
        ) : null}
        {block.adminNote ? (
          <p className="rounded border border-amber-100 bg-amber-50/80 px-2 py-1.5 text-gray-700">
            <span className="font-medium text-amber-900/90">
              {t("company.userRoles.helpLabel.admin")}
            </span>{" "}
            {block.adminNote}
          </p>
        ) : null}
      </div>
    );
  }
  const apiText =
    typeof fallbackDescription === "string" ? fallbackDescription.trim() : "";
  if (apiText) {
    return <p className="mt-1 text-xs leading-snug text-gray-500">{apiText}</p>;
  }
  return null;
}

function SyncRolesToFormik({
  roles,
  isLoading,
  itemToken,
  setFieldValue,
  idByName,
}) {
  const lastTokenRef = useRef(undefined);
  useEffect(() => {
    if (isLoading || !roles?.length || itemToken == null) return;
    if (lastTokenRef.current === itemToken) return;
    lastTokenRef.current = itemToken;
    setFieldValue(
      "roles",
      buildRoleFormPayloadFromTree(roles, { idByName: idByName || {} }),
    );
  }, [isLoading, roles, itemToken, setFieldValue, idByName]);
  return null;
}

// Seed the Projects access tier (projects_admin + selected full-access projects) from the user record.
function SyncProjectAccessToFormik({ item, itemToken, setFieldValue }) {
  const lastKeyRef = useRef(undefined);
  useEffect(() => {
    if (itemToken == null) return;

    const hasProjectsAdmin = (item?.roles || []).some(
      (r) => r.name === PROJECTS_ADMIN_ROLE,
    );
    const adminProjectIds = (item?.project_accesses || [])
      .filter((pa) => pa.admin)
      .map((pa) => pa.project_id)
      .filter((id) => id != null);

    // Re-seed when we switch users OR when the persisted grants change (e.g. after a
    // save refreshes `item`), so the picker/baseline never keep revoked or stale
    // admin grants. Keying on the grant signature (not just the user id) avoids the
    // "seed once per user" staleness after saving on the same user.
    const key = [
      itemToken,
      hasProjectsAdmin ? 1 : 0,
      [...adminProjectIds].map(String).sort().join(","),
    ].join("|");
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    setFieldValue("projects_admin", hasProjectsAdmin);
    // Company-wide admin supersedes per-project grants. Never copy stale
    // `project_accesses` into the picker/delete baseline when admin is on —
    // after save, `item.project_accesses` can lag while `roles` already flipped.
    if (hasProjectsAdmin) {
      setFieldValue("project_access_ids", []);
      setFieldValue("_original_admin_project_ids", []);
    } else {
      setFieldValue("project_access_ids", adminProjectIds);
      setFieldValue("_original_admin_project_ids", adminProjectIds);
    }
  }, [item, itemToken, setFieldValue]);
  return null;
}

// Projects access tier: module admin (all projects) or full access on selected projects.
function ProjectsAccessPanel({
  formik,
  projects,
  projectSearch,
  setProjectSearch,
  t,
}) {
  const projectsAdmin = Boolean(formik.values.projects_admin);
  const selectedIds = formik.values.project_access_ids || [];

  const toggleProject = (projectId) => {
    const exists = selectedIds.some((id) => sameId(id, projectId));
    formik.setFieldValue(
      "project_access_ids",
      exists
        ? selectedIds.filter((id) => !sameId(id, projectId))
        : [...selectedIds, projectId],
    );
  };

  return (
    <div className="mt-5 rounded-md border border-[#E2E8F0] bg-white shadow sm:overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-start gap-2">
          <FolderIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#1463C7]" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-[#0F172A]">
              {t("company.userRoles.projectsAccess.title")}
            </h3>
            <p className="mt-0.5 text-xs leading-snug text-[#64748B]">
              {t("company.userRoles.projectsAccess.intro")}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
          <PMSwitch
            name="projects_admin"
            checked={projectsAdmin}
            onChange={(e) =>
              formik.setFieldValue("projects_admin", e.target.checked)
            }
          />
          <div className="ml-3">
            <span className="text-sm font-semibold text-[#0F172A]">
              {t("company.userRoles.projectsAccess.adminToggle")}
            </span>
            <p className="mt-0.5 text-xs leading-snug text-[#64748B]">
              {t("company.userRoles.projectsAccess.adminHelp")}
            </p>
          </div>
        </div>

        {!projectsAdmin && (
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#334155]">
                {t("company.userRoles.projectsAccess.selectedLabel")}
              </span>
              {selectedIds.length > 0 && (
                <span className="rounded-full bg-[#EAF3FD] px-2 py-0.5 text-xs font-semibold text-[#0B3A78]">
                  {selectedIds.length}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs leading-snug text-[#64748B]">
              {t("company.userRoles.projectsAccess.selectedHelp")}
            </p>
            <div className="relative mt-2">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder={t(
                  "company.userRoles.projectsAccess.searchPlaceholder",
                )}
                className="w-full rounded-lg border border-[#E2E8F0] bg-white py-2 pl-9 pr-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#2D8BE8] focus:outline-none focus:ring-1 focus:ring-[#2D8BE8]"
              />
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-[#E2E8F0]">
              {projects.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-[#94A3B8]">
                  {t("company.userRoles.projectsAccess.noProjects")}
                </p>
              ) : (
                projects.map((project) => {
                  const checked = selectedIds.some((id) =>
                    sameId(id, project.id),
                  );
                  return (
                    <button
                      type="button"
                      key={project.id}
                      onClick={() => toggleProject(project.id)}
                      className={`flex w-full items-center gap-2 border-b border-[#EEF1F5] px-3 py-2 text-left last:border-b-0 transition hover:bg-[#F8FAFC] ${
                        checked ? "bg-[#F2F8FE]" : "bg-white"
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          checked
                            ? "border-[#1463C7] bg-[#1463C7] text-white"
                            : "border-[#CBD5E1] bg-white"
                        }`}
                      >
                        {checked && <CheckIcon className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-[#0F172A]">
                        {project.name || `#${project.id}`}
                      </span>
                      {project.project_number && (
                        <span className="shrink-0 text-xs text-[#94A3B8]">
                          {project.project_number}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Group modules into user-friendly categories so the role page reads as a small set of
// themed sections instead of one long flat list of cards.
const MODULE_CATEGORY = {
  admin: "admin",
  company: "admin",
  projects: "projects",
  hours: "time",
  absence: "time",
  absences: "time",
  calendar: "time",
  payslips: "time",
  service: "service",
  quality: "quality",
  hse: "quality",
  driving: "fleet",
  vehicles: "fleet",
  drawings: "drawings",
  ifc: "drawings",
  customers: "customers",
  phone: "customers",
  products: "sales",
  offers: "sales",
  orders: "sales",
  inventory: "sales",
};

const CATEGORY_ORDER = [
  "admin",
  "projects",
  "time",
  "service",
  "quality",
  "fleet",
  "drawings",
  "customers",
  "sales",
  "other",
];

const categoryForModule = (moduleName) =>
  MODULE_CATEGORY[moduleName] || "other";

// Active = module assigned, or any action / submodule selected underneath it.
// projects_admin lives in the Projects access panel (not the nested tree), so treat
// that Formik toggle as an active Projects grant when evaluating the module card.
const moduleIsActive = (module, { projectsAdmin = false } = {}) =>
  Boolean(
    module.assigned ||
    module.checked ||
    (module.actions || []).some((a) => a.checked) ||
    (module.submodules || []).some((s) => s.checked) ||
    (module.name === "projects" && projectsAdmin),
  );

const moduleSelectedCount = (module) =>
  (module.actions || []).filter((a) => a.checked).length +
  (module.submodules || []).filter((s) => s.checked).length;

// Compact collapsible module card: a header row (master switch + name + summary chip + chevron)
// that expands to reveal the granular actions and submodules.
function ModuleDisclosure({
  module,
  expanded,
  onToggleExpand,
  onRoleChange,
  projectsAdmin = false,
  t,
}) {
  const active = moduleIsActive(module, { projectsAdmin });
  const selected = moduleSelectedCount(module);
  const hasChildren =
    (module.actions || []).length > 0 || (module.submodules || []).length > 0;

  let summaryLabel;
  if (!active) summaryLabel = t("company.userRoles.summary.none");
  else if (selected === 0) summaryLabel = t("company.userRoles.summary.on");
  else
    summaryLabel = t("company.userRoles.summary.selected", { count: selected });

  return (
    <div
      className={`overflow-hidden rounded-xl border transition ${
        active
          ? "border-[#BFDCF7] bg-white shadow-sm"
          : "border-[#E9EEF4] bg-[#FBFCFE]"
      }`}
    >
      <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
        <PMSwitch
          name={module.name}
          // Bind to assigned (real grant), not visual checked — children must not
          // force the parent switch on or block granting the parent independently.
          checked={Boolean(module.assigned)}
          onChange={(e) => onRoleChange(e)}
        />
        <button
          type="button"
          onClick={hasChildren ? onToggleExpand : undefined}
          className={`flex min-w-0 flex-1 items-center gap-2 text-left ${
            hasChildren ? "cursor-pointer" : "cursor-default"
          }`}
        >
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#0F172A]">
            <UppercaseFirstLetter
              text={t(module.name, {
                defaultValue: module.label || module.name,
              })}
            />
          </span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              active
                ? "bg-[#EAF3FD] text-[#0B3A78]"
                : "bg-[#EEF1F5] text-[#94A3B8]"
            }`}
          >
            {summaryLabel}
          </span>
          {hasChildren && (
            <ChevronDownIcon
              className={`h-4 w-4 shrink-0 text-[#94A3B8] transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
      </div>

      {hasChildren && expanded && (
        <div className="border-t border-[#EEF1F5] bg-white px-3 py-3 sm:px-4">
          <RoleUserHelpText
            roleName={module.name}
            fallbackDescription={module.description}
          />

          {(module.actions || []).length > 0 && (
            <div className="mt-3">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("company.userRoles.sections.actions")}
              </h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {module.actions.map((action, actionIdx) => (
                  <div
                    key={actionIdx}
                    className="flex flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/50 p-2"
                  >
                    <div className="flex items-center">
                      <PMSwitch
                        name={action.name}
                        checked={action.checked}
                        onChange={(e) => onRoleChange(e)}
                      />
                      <UppercaseFirstLetter
                        text={t(action.action)}
                        className="ml-2 text-sm"
                      />
                    </div>
                    <RoleUserHelpText
                      roleName={action.name}
                      fallbackDescription={action.description}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(module.submodules || []).length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("company.userRoles.sections.submodules")}
              </h4>
              {module.submodules.map((submodule, subIdx) => (
                <div
                  key={subIdx}
                  className="mb-3 border-l-2 border-gray-200 pl-3 last:mb-0"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center">
                      <ArrowRightIcon className="mr-2 h-4 w-4 shrink-0 text-gray-400" />
                      <PMSwitch
                        name={submodule.name}
                        checked={submodule.checked}
                        onChange={(e) => onRoleChange(e)}
                      />
                      <UppercaseFirstLetter
                        text={t(submodule.name, {
                          defaultValue:
                            submodule.label ||
                            submodule.name.split("_")[1] ||
                            submodule.name,
                        })}
                        className="ml-2 text-sm"
                      />
                    </div>
                    <div className="ml-6">
                      <RoleUserHelpText
                        roleName={submodule.name}
                        fallbackDescription={submodule.description}
                      />
                    </div>
                  </div>

                  {submodule.checked &&
                    (submodule.actions || []).length > 0 && (
                      <div className="ml-6 mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {submodule.actions.map((action, actionIdx) => (
                          <div
                            key={actionIdx}
                            className="flex flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/50 p-2"
                          >
                            <div className="flex items-center">
                              <PMSwitch
                                name={action.name}
                                checked={action.checked}
                                onChange={(e) => onRoleChange(e)}
                              />
                              <UppercaseFirstLetter
                                text={t(action.action)}
                                className="ml-2 text-sm"
                              />
                            </div>
                            <RoleUserHelpText
                              roleName={action.name}
                              fallbackDescription={action.description}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const UserRoles = (props) => {
  const { t } = useTranslation();
  const {
    item,
    onRefresh,
    simulationMode = false,
    onSimulationApply,
    simulationItemKey,
  } = props;
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const projectsModuleActive =
    simulationMode || companyHasActiveFeature(currentCompany, "projects");

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [roleSearch, setRoleSearch] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  // Which module cards are expanded. `null` = not yet initialized for the current user.
  const [expandedModules, setExpandedModules] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(
    () => new Set(),
  );
  // name -> role id, so the template/projects_admin toggles can build the save payload.
  const roleIdByNameRef = useRef({});

  const fetchPMRoles = async () => {
    try {
      const res = await fetchRoles();
      if (res?.data?.data) {
        const map = {};
        res.data.data.forEach((r) => {
          if (r?.name) map[r.name] = r.id;
        });
        roleIdByNameRef.current = map;
        setMainRoles(res.data.data); // Call setMainRoles only if data exists
      } else {
        console.error("No roles data found");
        setIsLoading(false); // Stop loading if no data is found
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setIsLoading(false); // Stop loading on error
    }
  };

  useEffect(() => {
    let active = true;
    fetchRoleTemplates().then((list) => {
      if (active) setTemplates(Array.isArray(list) ? list : []);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!projectsModuleActive) return undefined;
    let active = true;
    const loadAllProjects = async () => {
      const perPage = 1000;
      let page = 1;
      let all = [];
      try {
        for (;;) {
          const res = await fetchProjects(
            buildProjectsIndexParams(
              {
                per_page: perPage,
                page,
                fields: "id,name,project_number,displayTitle",
              },
              { activeOnly: true },
            ),
          );
          if (!active) return;
          const data = Array.isArray(res?.data) ? res.data : [];
          all = all.concat(data);
          const total = Number(res?.total_count);
          const hasMore =
            data.length === perPage &&
            (Number.isFinite(total) ? all.length < total : true);
          if (!hasMore) break;
          page += 1;
        }
        if (active) setProjectsList(all);
      } catch {
        // Keep pages already fetched so a mid-pagination failure does not wipe the picker.
        if (active && all.length > 0) setProjectsList(all);
      }
    };
    loadAllProjects();
    return () => {
      active = false;
    };
  }, [projectsModuleActive]);

  // Find role by searching name
  const findUserRole = (name) => {
    const found = item?.roles?.find((role) => role.name === name);
    return found;
  };

  // Main Roles - Restructured for new hierarchy
  const setMainRoles = (roles) => {
    if (!Array.isArray(roles)) {
      console.error("Roles is not an array:", roles);
      setRoles([]);
      setIsLoading(false);
      return;
    }

    // Don't process roles if user data isn't loaded yet
    if (!item || !item.roles) {
      setRoles([]);
      setIsLoading(false);
      return;
    }

    // Group roles by main module
    const moduleGroups = {};

    // First, create modules for all user roles that don't exist in the database
    item.roles.forEach((userRole) => {
      const parsed = parseRoleName(userRole.name);

      if (parsed.type === "main" && !moduleGroups[parsed.module]) {
        moduleGroups[parsed.module] = {
          id: userRole.id || null,
          name: userRole.name,
          label: userRole.label,
          description: userRole.description,
          checked: true, // User has this role
          actions: [],
          submodules: {},
        };
      } else if (parsed.type === "submodule" && !moduleGroups[parsed.module]) {
        // Create parent module if it doesn't exist
        moduleGroups[parsed.module] = {
          name: parsed.module,
          checked: false,
          actions: [],
          submodules: {},
        };
      }

      if (parsed.type === "submodule") {
        // Ensure parent module exists
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }

        // Create sub-module
        if (!moduleGroups[parsed.module].submodules[parsed.submodule]) {
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            id: userRole.id || null,
            name: userRole.name,
            label: userRole.label,
            description: userRole.description,
            checked: true, // User has this role
            actions: [],
          };
        }
      }
    });

    // Then process all database roles
    roles.forEach((role) => {
      if (!role || !role.name) {
        console.warn("Invalid role found:", role);
        return;
      }

      const parsed = parseRoleName(role.name);

      if (parsed.type === "main") {
        // Main module
        if (!moduleGroups[parsed.module]) {
          const isChecked = findUserRole(role.name) ? true : false;
          moduleGroups[parsed.module] = {
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            actions: [],
            submodules: {},
          };
        } else {
          // Update existing module with database info
          moduleGroups[parsed.module] = {
            ...moduleGroups[parsed.module],
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
          };
        }
      } else if (parsed.type === "action") {
        // Module action
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        const isChecked = findUserRole(role.name) ? true : false;
        const existingAction = moduleGroups[parsed.module].actions.find(
          (a) => a.name === role.name,
        );
        if (existingAction) {
          existingAction.id = role.id;
          existingAction.label = role.label;
          existingAction.description = role.description;
        } else {
          moduleGroups[parsed.module].actions.push({
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            action: parsed.action,
          });
        }
      } else if (parsed.type === "submodule") {
        // Sub-module
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        const existingSub =
          moduleGroups[parsed.module].submodules[parsed.submodule];
        if (!existingSub) {
          const isChecked = findUserRole(role.name) ? true : false;
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            actions: [],
          };
        } else {
          // Catalog wins for id/name — user.roles has no Role id today, and
          // submodule_action rows can create a placeholder without id first.
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            ...existingSub,
            id: role.id,
            name: role.name,
            label: role.label ?? existingSub.label,
            description: role.description ?? existingSub.description,
          };
        }
      } else if (parsed.type === "submodule_action") {
        // Sub-module action
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        if (!moduleGroups[parsed.module].submodules[parsed.submodule]) {
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            name: `${parsed.module}_${parsed.submodule}`,
            checked: false,
            actions: [],
          };
        }
        const isChecked = findUserRole(role.name) ? true : false;
        const sub = moduleGroups[parsed.module].submodules[parsed.submodule];
        const existingAction = (sub.actions || []).find(
          (a) => a.name === role.name,
        );
        if (existingAction) {
          existingAction.id = role.id;
          existingAction.label = role.label;
          existingAction.description = role.description;
        } else {
          sub.actions.push({
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            action: parsed.action,
          });
        }
      }
    });

    // Convert to array format and set proper visual indicators
    const main = Object.values(moduleGroups).map((module) => {
      // Calculate if main module should be visually checked based on children
      const hasCheckedActions = (module.actions || []).some(
        (action) => action.checked,
      );
      const hasCheckedSubmodules = Object.values(module.submodules || {}).some(
        (sub) => sub.checked,
      );
      // assigned = actually granted; checked = visual (assigned OR children on)
      const assigned = Boolean(module.checked);
      const mainModuleChecked =
        assigned || hasCheckedActions || hasCheckedSubmodules;

      // Update submodules with proper visual indicators.
      // projects_admin is surfaced in a dedicated Projects access panel, so keep it out of the
      // generic nested tree to avoid a confusing "admin" sub-toggle under Projects.
      const updatedSubmodules = Object.values(module.submodules || {})
        .filter((submodule) => submodule.name !== PROJECTS_ADMIN_ROLE)
        .map((submodule) => {
          const submoduleAssigned = Boolean(submodule.checked);
          return {
            ...submodule,
            assigned: submoduleAssigned,
            checked: submoduleAssigned,
          };
        });

      return {
        ...module,
        assigned,
        checked: mainModuleChecked,
        submodules: updatedSubmodules,
      };
    });

    setRoles(main);
    setIsLoading(false); // Ensure loading is stopped after roles are set
  };

  useEffect(() => {
    // If we have user data and roles are already loaded, reprocess them
    if (item && item.roles && roles.length > 0) {
      // We need to refetch roles to reprocess them with the user data
      fetchPMRoles();
    }
  }, [item]);

  useEffect(() => {
    fetchPMRoles(); // Fetch roles on mount
  }, []);

  // Re-initialize the expanded/collapsed state whenever we switch to a different user.
  useEffect(() => {
    setExpandedModules(null);
    setCollapsedCategories(new Set());
  }, [item?.id, simulationItemKey]);

  // Default expand state: only modules that already have access are open. Used before the
  // init effect runs (expandedModules === null) so render and the first chevron toggle stay
  // consistent — a null state must not read as "every module expanded", otherwise the first
  // click collapses everything except the clicked module instead of toggling that one row.
  const defaultExpandedModuleNames = (projectsAdmin = false) => {
    const next = new Set();
    roles.forEach((m) => {
      if (moduleIsActive(m, { projectsAdmin })) next.add(m.name);
    });
    return next;
  };

  // First time roles are ready for this user, auto-expand only the modules that already
  // have access granted — everything else stays collapsed to keep the page short.
  useEffect(() => {
    if (!roles.length) return;
    const itemHasProjectsAdmin = (item?.roles || []).some(
      (r) => r.name === PROJECTS_ADMIN_ROLE,
    );
    setExpandedModules((prev) =>
      prev ? prev : defaultExpandedModuleNames(itemHasProjectsAdmin),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const handleRoleChange = (e, formik) => {
    const roleName = e.target.name;
    const checked = e.target.checked;
    const parsed = parseRoleName(roleName);

    let newRoles = [...roles];

    if (parsed.type === "main") {
      // Parent switch toggles assigned (+ module-level actions). Submodules stay independent.
      newRoles = roles.map((role) =>
        role.name === roleName ? applyMainModuleToggle(role, checked) : role,
      );
    } else if (parsed.type === "action") {
      // Module action toggle
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newActions = (role.actions || []).map((action) =>
            action.name === roleName
              ? { ...action, assigned: checked, checked: checked }
              : action,
          );
          const hasAnyChecked =
            newActions.some((action) => action.checked) ||
            (role.submodules || []).some((sub) => sub.checked);
          return {
            ...role,
            // Keep assigned as the real grant; checked is visual only.
            checked: Boolean(role.assigned) || hasAnyChecked,
            actions: newActions,
          };
        }
        return role;
      });
    } else if (parsed.type === "submodule") {
      // Sub-module toggle - only affects sub-module actions
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newSubmodules = (role.submodules || []).map((submodule) =>
            submodule.name === roleName
              ? {
                  ...submodule,
                  assigned: checked,
                  checked: checked,
                  actions: (submodule.actions || []).map((action) => ({
                    ...action,
                    assigned: checked,
                    checked: checked,
                  })),
                }
              : submodule,
          );
          const hasAnyChecked =
            (role.actions || []).some((action) => action.checked) ||
            newSubmodules.some((sub) => sub.checked);
          return {
            ...role,
            checked: Boolean(role.assigned) || hasAnyChecked,
            submodules: newSubmodules,
          };
        }
        return role;
      });
    } else if (parsed.type === "submodule_action") {
      // Sub-module action toggle
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newSubmodules = (role.submodules || []).map((submodule) => {
            if (submodule.name === `${parsed.module}_${parsed.submodule}`) {
              const newActions = (submodule.actions || []).map((action) =>
                action.name === roleName
                  ? { ...action, assigned: checked, checked: checked }
                  : action,
              );
              const submoduleAssigned = Boolean(submodule.assigned);
              return {
                ...submodule,
                assigned: submoduleAssigned,
                checked: submoduleAssigned,
                actions: newActions,
              };
            }
            return submodule;
          });
          const hasAnyChecked =
            (role.actions || []).some((action) => action.checked) ||
            newSubmodules.some((sub) => sub.checked);
          return {
            ...role,
            checked: Boolean(role.assigned) || hasAnyChecked,
            submodules: newSubmodules,
          };
        }
        return role;
      });
    }

    // Update the state first
    setRoles(newRoles);

    formik.setFieldValue(
      "roles",
      buildRoleFormPayloadFromTree(newRoles, {
        idByName: roleIdByNameRef.current,
      }),
    );
  };

  // Build the save payload: merge the projects_admin toggle and per-project full-access grants
  // into the roles / project_accesses shape the API expects.
  // When projects_admin cannot be resolved to a catalog role id, omit that toggle from the
  // payload (do not abort) so unrelated role/approval edits still save.
  const buildSavePayload = (values) => {
    const idByName = roleIdByNameRef.current || {};
    const baseRoles = (values.roles || [])
      .filter((r) => r.name !== PROJECTS_ADMIN_ROLE)
      .map((r) => ({
        id: r.id ?? idByName[r.name],
        name: r.name,
        active: Boolean(r.active),
      }))
      .filter((r) => r.id != null);
    const projectsAdminId =
      idByName[PROJECTS_ADMIN_ROLE] ??
      item?.roles?.find((r) => r.name === PROJECTS_ADMIN_ROLE)?.id;
    const wantProjectsAdmin = Boolean(values.projects_admin);
    if (projectsAdminId != null) {
      baseRoles.push({
        id: projectsAdminId,
        name: PROJECTS_ADMIN_ROLE,
        active: wantProjectsAdmin,
      });
    }

    let projectAccesses;
    if (!values.projects_admin) {
      const selected = values.project_access_ids || [];
      const original = values._original_admin_project_ids || [];
      const selectedKeys = new Set(selected.map((id) => String(id)));
      const upserts = selected.map((id) => ({
        project_id: id,
        admin: true,
        role: "RESPONSIBLE",
      }));
      const deletes = original
        .filter((id) => !selectedKeys.has(String(id)))
        .map((id) => ({ project_id: id, delete: true }));
      projectAccesses = [...upserts, ...deletes];
    } else {
      // Company-wide admin supersedes per-project grants — explicitly delete any that
      // were previously persisted so server state matches the form (and the post-save
      // baseline reset to [] remains correct).
      const original = values._original_admin_project_ids || [];
      projectAccesses = original.map((id) => ({
        project_id: id,
        delete: true,
      }));
    }

    // Only send API fields — strip Formik/UI-only keys (projects_admin, project_access_ids, …).
    return {
      id: values.id,
      approved: values.approved,
      roles: baseRoles,
      project_accesses: projectAccesses,
    };
  };

  // Apply a system template: replace the current role selection with the template bundle.
  const applyTemplate = (template, formik) => {
    if (!template) return;
    const templateRoleSet = new Set(template.roles || []);

    // Rebuild the nested tree checked-state from the template roles.
    const newTree = roles.map((module) => {
      const moduleAssigned = templateRoleSet.has(module.name);
      const actions = (module.actions || []).map((a) => {
        const on = templateRoleSet.has(a.name);
        return { ...a, assigned: on, checked: on };
      });
      const submodules = (module.submodules || []).map((sub) => {
        const subAssigned = templateRoleSet.has(sub.name);
        return {
          ...sub,
          assigned: subAssigned,
          checked: subAssigned,
          actions: (sub.actions || []).map((a) => {
            const on = templateRoleSet.has(a.name);
            return { ...a, assigned: on, checked: on };
          }),
        };
      });
      const anyChild =
        actions.some((a) => a.checked) || submodules.some((s) => s.checked);
      return {
        ...module,
        assigned: moduleAssigned,
        checked: moduleAssigned || anyChild,
        actions,
        submodules,
      };
    });
    setRoles(newTree);
    formik.setFieldValue(
      "roles",
      buildRoleFormPayloadFromTree(newTree, {
        idByName: roleIdByNameRef.current,
      }),
    );

    // Projects tier from the template's projects_scope. Always clear individual
    // project grants so a narrower template cannot leave stale selections that
    // would still be upserted on save. Keep `_original_admin_project_ids` so
    // previously persisted grants are deleted when the form is saved.
    const isProjectsAdmin =
      templateRoleSet.has(PROJECTS_ADMIN_ROLE) ||
      template.projects_scope === "all";
    formik.setFieldValue("projects_admin", isProjectsAdmin);
    formik.setFieldValue("project_access_ids", []);
    toast.success(t("company.userRoles.templates.applied"));
  };

  // Active projects plus any currently granted (possibly inactive) projects so admins
  // can clear grants that would otherwise stay selected but invisible in the picker.
  const projectsForPicker = useMemo(() => {
    const byId = new Map();
    (projectsList || []).forEach((p) => {
      if (p?.id != null) byId.set(String(p.id), p);
    });
    (item?.project_accesses || []).forEach((pa) => {
      if (!pa?.admin || pa.project_id == null) return;
      const key = String(pa.project_id);
      if (byId.has(key)) return;
      const nested = pa.project || {};
      byId.set(key, {
        id: pa.project_id,
        name: nested.name || pa.project_name || `#${pa.project_id}`,
        project_number: nested.project_number || pa.project_number,
      });
    });
    return Array.from(byId.values());
  }, [projectsList, item?.project_accesses]);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projectsForPicker;
    return projectsForPicker.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const number = String(p.project_number || "").toLowerCase();
      return name.includes(q) || number.includes(q);
    });
  }, [projectsForPicker, projectSearch]);

  const matchesRoleSearch = (module) => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return true;
    const nodeMatches = (node) => {
      if (!node?.name) return false;
      const label = t(node.name, {
        defaultValue: node.label || node.name,
      });
      return (
        node.name.toLowerCase().includes(q) ||
        String(label).toLowerCase().includes(q)
      );
    };
    if (nodeMatches(module)) return true;
    if ((module.actions || []).some(nodeMatches)) return true;
    return (module.submodules || []).some(
      (sub) => nodeMatches(sub) || (sub.actions || []).some(nodeMatches),
    );
  };

  // Modules that match the search, grouped by category, in a stable display order.
  const groupedCategories = useMemo(() => {
    const visible = roles.filter(matchesRoleSearch);
    const byCategory = {};
    visible.forEach((module) => {
      const cat = categoryForModule(module.name);
      (byCategory[cat] = byCategory[cat] || []).push(module);
    });
    return CATEGORY_ORDER.filter((cat) => byCategory[cat]?.length).map(
      (cat) => ({ id: cat, modules: byCategory[cat] }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, roleSearch]);

  const searchActive = roleSearch.trim().length > 0;

  const isModuleExpanded = (moduleName, projectsAdmin = false) => {
    if (searchActive) return true;
    const set = expandedModules || defaultExpandedModuleNames(projectsAdmin);
    return set.has(moduleName);
  };

  const toggleModuleExpanded = (moduleName, projectsAdmin = false) => {
    setExpandedModules((prev) => {
      // Seed from the default set (not an empty set) when uninitialized, so the first
      // toggle flips only the clicked module instead of collapsing all the others.
      const next = new Set(prev || defaultExpandedModuleNames(projectsAdmin));
      if (next.has(moduleName)) next.delete(moduleName);
      else next.add(moduleName);
      return next;
    });
  };

  const isCategoryCollapsed = (categoryId) =>
    !searchActive && collapsedCategories.has(categoryId);

  const toggleCategory = (categoryId) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <Formik
        initialValues={{
          id: item?.id,
          approved: simulationMode ? true : item?.approved,
          submit: null,
          roles: [],
          projects_admin: false,
          project_access_ids: [],
          _original_admin_project_ids: [],
        }}
        onSubmit={(values, helpers) => {
          const payload = buildSavePayload(values);
          const projectsAdminId =
            roleIdByNameRef.current[PROJECTS_ADMIN_ROLE] ??
            item?.roles?.find((r) => r.name === PROJECTS_ADMIN_ROLE)?.id;
          const wantProjectsAdmin = Boolean(values.projects_admin);
          const hadProjectsAdmin = (item?.roles || []).some(
            (r) => r.name === PROJECTS_ADMIN_ROLE,
          );
          // Warn when the admin toggle cannot be applied, but still persist other edits.
          if (
            projectsAdminId == null &&
            wantProjectsAdmin !== hadProjectsAdmin
          ) {
            toast.error(t("company.userRoles.projectsAccess.saveMissingRole"));
          }
          if (simulationMode) {
            const active = (payload.roles || [])
              .filter((r) => r.active)
              .map((r) => r.name);
            // Reflect the Projects access toggle even when the catalog role id
            // could not be resolved into the save payload.
            if (wantProjectsAdmin && !active.includes(PROJECTS_ADMIN_ROLE)) {
              active.push(PROJECTS_ADMIN_ROLE);
            }
            // Per-project grants only apply when company-wide admin is off.
            const projectAccessIds = wantProjectsAdmin
              ? []
              : [...(values.project_access_ids || [])];
            onSimulationApply?.(active, projectAccessIds);
            helpers.setSubmitting(false);
            return;
          }
          try {
            dispatchWithToast(dispatch(updateUserAsync(payload)), (data) => {
              onRefresh?.(data);
              // Refresh the delete baseline after save so grants added in this
              // session can be revoked later. When company-wide admin is on the
              // baseline is empty; SyncProjectAccessToFormik must not overwrite
              // this with stale `item.project_accesses`.
              helpers.setFieldValue(
                "_original_admin_project_ids",
                values.projects_admin
                  ? []
                  : [...(values.project_access_ids || [])],
              );
            });
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <SyncRolesToFormik
              roles={roles}
              isLoading={isLoading}
              itemToken={
                simulationMode
                  ? (simulationItemKey ?? `sim-${item?.id}`)
                  : item?.id
              }
              setFieldValue={formik.setFieldValue}
              idByName={roleIdByNameRef.current}
            />
            <SyncProjectAccessToFormik
              item={item}
              itemToken={
                simulationMode
                  ? (simulationItemKey ?? `sim-${item?.id}`)
                  : item?.id
              }
              setFieldValue={formik.setFieldValue}
            />
            {!simulationMode && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                <div className=" bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="flex text-lg font-medium leading-6 text-gray-900">
                      <PMSwitch name="approved" {...formik} />
                      {t("company.users.roles.has_access")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("company.userRoles.accessIntro")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(simulationMode || formik.values.approved) &&
              templates.length > 0 && (
                <div className="mt-5 rounded-md border border-[#BFDCF7] bg-[#EAF3FD] px-4 py-4 sm:px-6">
                  <div className="flex items-start gap-2">
                    <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#1463C7]" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-[#0B3A78]">
                        {t("company.userRoles.templates.title")}
                      </h3>
                      <p className="mt-0.5 text-xs text-[#334155]">
                        {t("company.userRoles.templates.intro")}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {templates.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        title={tpl.description}
                        onClick={() => applyTemplate(tpl, formik)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#BFDCF7] bg-white px-3 py-1.5 text-xs font-semibold text-[#0B3A78] shadow-sm transition hover:border-[#2D8BE8] hover:bg-[#F2F8FE]"
                      >
                        <CheckIcon className="h-3.5 w-3.5 text-[#1463C7]" />
                        {tpl.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {(simulationMode || formik.values.approved) &&
              projectsModuleActive && (
                <ProjectsAccessPanel
                  formik={formik}
                  projects={filteredProjects}
                  projectSearch={projectSearch}
                  setProjectSearch={setProjectSearch}
                  t={t}
                />
              )}

            {(simulationMode || formik.values.approved) && roles.length > 0 && (
              <div className="mt-5">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder={t("company.userRoles.searchPlaceholder")}
                    className="w-full rounded-lg border border-[#E2E8F0] bg-white py-2 pl-9 pr-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#2D8BE8] focus:outline-none focus:ring-1 focus:ring-[#2D8BE8]"
                  />
                </div>
              </div>
            )}

            {(simulationMode || formik.values.approved) &&
              groupedCategories.length === 0 &&
              searchActive && (
                <p className="mt-5 rounded-lg border border-dashed border-[#E2E8F0] bg-[#FBFCFE] px-4 py-6 text-center text-sm text-[#94A3B8]">
                  {t("company.userRoles.noMatches")}
                </p>
              )}

            {(simulationMode || formik.values.approved) &&
              groupedCategories.map((category) => {
                const collapsed = isCategoryCollapsed(category.id);
                const projectsAdmin = Boolean(formik.values.projects_admin);
                const activeCount = category.modules.filter((m) =>
                  moduleIsActive(m, { projectsAdmin }),
                ).length;
                return (
                  <div key={category.id} className="mt-6">
                    <button
                      type="button"
                      onClick={() => toggleCategory(category.id)}
                      className="flex w-full items-center gap-2 border-b border-[#E9EEF4] pb-2 text-left"
                    >
                      <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 text-[#94A3B8] transition-transform ${
                          collapsed ? "-rotate-90" : ""
                        }`}
                      />
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#334155]">
                        {t(`company.userRoles.categories.${category.id}`, {
                          defaultValue: category.id,
                        })}
                      </h3>
                      {activeCount > 0 && (
                        <span className="rounded-full bg-[#EAF3FD] px-2 py-0.5 text-[11px] font-semibold text-[#0B3A78]">
                          {activeCount}
                        </span>
                      )}
                    </button>

                    {!collapsed && (
                      <div className="mt-3 space-y-2.5">
                        {category.modules.map((module) => (
                          <ModuleDisclosure
                            key={module.name}
                            module={module}
                            expanded={isModuleExpanded(
                              module.name,
                              projectsAdmin,
                            )}
                            onToggleExpand={() =>
                              toggleModuleExpanded(module.name, projectsAdmin)
                            }
                            onRoleChange={(e) => handleRoleChange(e, formik)}
                            projectsAdmin={projectsAdmin}
                            t={t}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            {!simulationMode && <FormikSaveBar {...formik} />}
            {simulationMode && (
              <div className="mt-4 flex justify-end border-t border-gray-100 pt-4">
                <button
                  type="submit"
                  className="rounded-md bg-violet-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                >
                  {t("platformBroadcast.dev.roleViewCustomApply")}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};
